import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/membros/diagnostico")({
  head: () => ({ meta: [{ title: "Diagnóstico — Projeto 101" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: DiagnosticPage,
});

type Answers = {
  budget?: "menos1k" | "1a3k" | "3a10k" | "mais10k";
  time?: "menos10" | "10a20" | "mais20";
  taste?: "pessoas" | "produtos" | "internet";
  place?: "casa" | "cliente" | "online" | "ponto";
  team?: "solo" | "equipe";
  skill?: "servicos" | "comida" | "beleza" | "pet" | "digital" | "ensino" | "artesanato" | "eventos" | "auto" | "b2b";
};

const questions: {
  key: keyof Answers;
  title: string;
  options: { value: string; label: string }[];
}[] = [
  {
    key: "budget",
    title: "Quanto você tem disponível para investir agora?",
    options: [
      { value: "menos1k", label: "Menos de R$ 1.000" },
      { value: "1a3k", label: "R$ 1.000 a R$ 3.000" },
      { value: "3a10k", label: "R$ 3.000 a R$ 10.000" },
      { value: "mais10k", label: "Mais de R$ 10.000" },
    ],
  },
  {
    key: "time",
    title: "Quantas horas por semana consegue dedicar?",
    options: [
      { value: "menos10", label: "Menos de 10h" },
      { value: "10a20", label: "10 a 20h" },
      { value: "mais20", label: "Mais de 20h" },
    ],
  },
  {
    key: "taste",
    title: "Você prefere trabalhar com...",
    options: [
      { value: "pessoas", label: "Pessoas (atendimento presencial)" },
      { value: "produtos", label: "Produtos (fazer, montar, vender)" },
      { value: "internet", label: "Internet (online, remoto)" },
    ],
  },
  {
    key: "place",
    title: "Onde você pretende trabalhar?",
    options: [
      { value: "casa", label: "Em casa" },
      { value: "cliente", label: "No local do cliente" },
      { value: "online", label: "100% online" },
      { value: "ponto", label: "Ponto comercial próprio" },
    ],
  },
  {
    key: "team",
    title: "Você quer começar sozinho ou com equipe?",
    options: [
      { value: "solo", label: "Sozinho(a)" },
      { value: "equipe", label: "Com equipe desde o início" },
    ],
  },
  {
    key: "skill",
    title: "Em qual área você tem mais experiência ou afinidade?",
    options: [
      { value: "servicos", label: "Serviços residenciais" },
      { value: "comida", label: "Alimentação" },
      { value: "beleza", label: "Beleza e bem-estar" },
      { value: "pet", label: "Mercado pet" },
      { value: "digital", label: "Marketing e digital" },
      { value: "ensino", label: "Educação e carreira" },
      { value: "artesanato", label: "Comércio e artesanal" },
      { value: "eventos", label: "Eventos e turismo" },
      { value: "auto", label: "Automotivo e logística" },
      { value: "b2b", label: "Negócios B2B" },
    ],
  },
];

const skillToCategorySlug: Record<string, string> = {
  servicos: "servicos-residenciais",
  comida: "alimentacao",
  beleza: "beleza-bem-estar",
  pet: "mercado-pet",
  digital: "marketing-digital",
  ensino: "educacao-carreira",
  artesanato: "comercio-artesanal",
  eventos: "eventos-turismo",
  auto: "automotivo-logistica",
  b2b: "b2b-operacoes",
};

const budgetMax: Record<string, number> = { menos1k: 100000, "1a3k": 300000, "3a10k": 1000000, mais10k: 999999999 };

function DiagnosticPage() {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<{ ids: string[]; catNames: string[] } | null>(null);

  const { data: last } = useQuery({
    queryKey: ["last-diagnostic-full"],
    queryFn: async () => {
      const { data } = await supabase
        .from("diagnostic_results")
        .select("*, businesses:businesses!inner(id, slug, title, summary)")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  function pick(value: string) {
    const q = questions[step];
    const next: Answers = { ...answers, [q.key]: value as any };
    setAnswers(next);
    if (step < questions.length - 1) setStep(step + 1);
    else finalize(next);
  }

  async function finalize(a: Answers) {
    const catSlug = a.skill ? skillToCategorySlug[a.skill] : null;
    const maxInv = a.budget ? budgetMax[a.budget] : 999999999;

    let q = supabase.from("businesses").select("id, slug, title, summary, category:categories(name, slug)").eq("active", true);
    if (catSlug) q = q.eq("category.slug", catSlug);
    q = q.lte("investment_min_cents", maxInv);
    if (a.team === "solo") q = q.eq("needs_team", false);
    if (a.place === "casa") q = q.eq("work_from_home", true);
    if (a.place === "online" || a.taste === "internet") q = q.eq("work_online", true);
    if (a.place === "cliente") q = q.eq("on_site", true);

    let { data: recs } = await q.limit(5);
    if (!recs || recs.length < 3) {
      const { data: fallback } = await supabase.from("businesses").select("id, slug, title, summary, category:categories(name, slug)").eq("active", true).lte("investment_min_cents", maxInv).limit(5);
      recs = fallback ?? [];
    }
    const ids = (recs ?? []).map((r) => r.id);
    const catNames = Array.from(new Set((recs ?? []).map((r: any) => r.category?.name).filter(Boolean)));

    const summary = `Perfil: ${a.taste ?? "-"} · ${a.place ?? "-"} · ${a.team ?? "-"}. ${catNames.slice(0, 2).join(" e ") || "Categorias variadas"}.`;

    const { data: userRes } = await supabase.auth.getUser();
    if (userRes.user) {
      await supabase.from("diagnostic_results").insert({
        user_id: userRes.user.id,
        answers: a,
        recommended_businesses: ids,
        summary,
      });
    }
    setResult({ ids, catNames });
    qc.invalidateQueries({ queryKey: ["last-diagnostic"] });
    qc.invalidateQueries({ queryKey: ["last-diagnostic-full"] });
  }

  function reset() {
    setStep(0);
    setAnswers({});
    setResult(null);
  }

  if (result) {
    return <ResultView ids={result.ids} categoryNames={result.catNames} onReset={reset} />;
  }

  const q = questions[step];

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Diagnóstico</h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          Responda 6 perguntas rápidas para ver de 3 a 5 negócios compatíveis com seu perfil.
        </p>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
        <div className="h-full bg-[var(--green)] transition-all" style={{ width: `${((step + 1) / questions.length) * 100}%` }} />
      </div>

      <div className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          Pergunta {step + 1} de {questions.length}
        </p>
        <h2 className="mt-1 font-display text-2xl font-extrabold">{q.title}</h2>
        <div className="mt-6 space-y-2">
          {q.options.map((o) => (
            <button
              key={o.value}
              onClick={() => pick(o.value)}
              className="w-full rounded-md border border-black/10 bg-white px-4 py-3 text-left text-sm transition hover:border-[var(--green)] hover:bg-[var(--green)]/5"
            >
              {o.label}
            </button>
          ))}
        </div>
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="mt-4 text-xs text-[var(--gray-ink)] hover:underline">
            ← Voltar
          </button>
        )}
      </div>

      {last && (
        <div className="rounded-2xl border border-black/5 bg-[var(--beige)] p-5 text-sm">
          <p className="font-semibold">Último resultado salvo</p>
          <p className="mt-1 text-[var(--gray-ink)]">{last.summary}</p>
        </div>
      )}
    </div>
  );
}

function ResultView({ ids, categoryNames, onReset }: { ids: string[]; categoryNames: string[]; onReset: () => void }) {
  const { data: recs } = useQuery({
    queryKey: ["diag-recs", ids.join(",")],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const { data } = await supabase.from("businesses").select("id, slug, title, summary, category:categories(name)").in("id", ids);
      return data ?? [];
    },
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8">
        <h1 className="font-display text-3xl font-extrabold">Seu resultado</h1>
        <p className="mt-2 text-[var(--gray-ink)]">
          Com base nas suas respostas, estas são as recomendações mais compatíveis.
        </p>
        {categoryNames.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {categoryNames.map((c) => (
              <span key={c} className="rounded-full bg-[var(--green)]/10 px-3 py-1 text-xs font-semibold text-[var(--green)]">
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {(recs ?? []).map((b: any) => (
          <Link
            key={b.id}
            to="/membros/negocios/$slug"
            params={{ slug: b.slug }}
            className="rounded-2xl border border-black/5 bg-white p-5 hover:border-[var(--green)]"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--green)]">{b.category?.name}</p>
            <h3 className="mt-1 font-display text-lg font-extrabold">{b.title}</h3>
            <p className="mt-2 text-sm text-[var(--gray-ink)]">{b.summary}</p>
          </Link>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={onReset} className="rounded-md border border-black/10 px-4 py-2 text-sm font-semibold">Refazer</button>
        <Link to="/membros" className="rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">Voltar ao painel</Link>
      </div>
    </div>
  );
}
